import React from 'react';
import { useTheme } from 'styled-components';

import { Box, Flex } from '@rover/kibble/core';

import { MOBILE_MAX_WIDTH_LAYOUT } from './constants';

const sectionStyle = {
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100px',
  border: '2px solid',
  borderColor: '#ffff',
};

const SCROLL_MARGIN_WITH_SEPARATOR = '52px'; // 40px of spacing + 12px above the title
const SCROLL_MARGIN_WITHOUT_SEPARATOR = '12px'; // Just 12px above the title

export const PageLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex flexDirection="column" alignItems="center" width="100%">
      <Box
        width="100%"
        maxWidth={[MOBILE_MAX_WIDTH_LAYOUT, 'initial', 'initial']}
        paddingX={['4x', '2x']}
      >
        {props.children}
      </Box>
    </Flex>
  );
};

export const HeaderContainerLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex flexDirection="column" alignItems="center" width="100%">
      <Box
        width="100%"
        maxWidth={[MOBILE_MAX_WIDTH_LAYOUT, 'initial', 'initial']}
        paddingX={['0x', '2x']}
      >
        {props.children}
      </Box>
    </Flex>
  );
};

export const HeaderLayout = React.forwardRef(
  (props: { children: React.ReactNode }, ref: React.ForwardedRef<HTMLElement>): JSX.Element => {
    return (
      // A Box with display="flex" is used instead of a Flex because the latter cannot accept a ref.
      <Box display="flex" flexDirection={['column', 'column', 'row']} width="100%" ref={ref}>
        {props.children}
      </Box>
    );
  }
);

export const ProfileLayout = (props: { children?: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      sx={{ order: [2, 2, 1] }}
      width={['100%', '100%', '352px']}
      maxWidth={[MOBILE_MAX_WIDTH_LAYOUT, 'initial']}
      position="relative"
      top={['0', '-56px', '0']}
      pt={['6x', '0x']}
      pb={['0x', '6x', '0x']}
      paddingX={['4x', '0x']}
      zIndex="999"
    >
      {props.children}
    </Flex>
  );
};

export const GalleryLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      sx={{ order: [1, 1, 2] }}
      pl={['0x', '0x', '12x']}
      width={['100%', '100%', 'initial']}
      flex="1"
    >
      {props.children}
    </Flex>
  );
};

export const LeftColumnLayout = (props: { children: React.ReactNode }): JSX.Element => (
  <Flex
    sx={{ order: [2, 2, 1], pb: ['0px', '40px', '0px'] }}
    flexDirection="column"
    width={['100%', '100%', '352px']}
    marginRight={['0x', '0x', '6x']}
    alignItems="start"
    alignSelf="stretch"
  >
    {props.children}
  </Flex>
);

export const PricingAvailabilityLayout = (props: { children: React.ReactNode }): JSX.Element => {
  const { colors } = useTheme();
  return (
    <Flex
      sx={{ boxShadow: ['none', 'none', '0px 1px 4px 0px rgba(27, 31, 35, 0.32)'] }}
      flexDirection="column"
      borderRadius="8px"
      paddingX={['0x', '0x', '4x']}
      paddingY={['0x', '0x', '8x']}
      border={['none', 'none', `1px solid ${colors.separator.primary}`]}
      background="primary"
      width="100%"
    >
      {props.children}
    </Flex>
  );
};

export const ServiceLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      {...sectionStyle}
      alignItems="start"
      width={['100%', '50%', '100%']}
      id="services"
      sx={{ scrollMarginTop: SCROLL_MARGIN_WITH_SEPARATOR }}
      paddingBottom="10x"
      data-testid="service-section"
    >
      {props.children}
    </Flex>
  );
};

export const AvailabilityLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      {...sectionStyle}
      alignItems="start"
      width={['100%', '50%', '100%']}
      sx={{ marginLeft: ['0x', '56px', '0x'], scrollMarginTop: SCROLL_MARGIN_WITHOUT_SEPARATOR }}
      data-testid="sitter-availability"
    >
      {props.children}
    </Flex>
  );
};

export const SkillsAndRestTableLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      display={['flex', 'flex', 'none']}
      flexDirection="column"
      id="more-info"
      sx={{ scrollMarginTop: SCROLL_MARGIN_WITH_SEPARATOR }}
    >
      {props.children}
    </Flex>
  );
};

export const SkillsAndRestDesktopLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex flexDirection="column" display={['none', 'none', 'flex']}>
      {props.children}
    </Flex>
  );
};

export const MoreSittersLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      background="secondary"
      paddingX="4x"
      paddingY="12x"
      flexDirection="column"
      alignItems="center"
      alignSelf="stretch"
    >
      <Flex
        maxWidth={[MOBILE_MAX_WIDTH_LAYOUT, 'initial']}
        flexDirection="column"
        alignItems="center"
        width="100%"
      >
        {props.children}
      </Flex>
    </Flex>
  );
};

export const MoreInfoLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Box pb="24x">
      <Box pb="2x">{props.children}</Box>
    </Box>
  );
};

export const SectionWithBottomSeparator = (props: { children: React.ReactNode }): JSX.Element => {
  const { colors, borderWidth } = useTheme();
  return (
    <Box
      pb="10x"
      mb="10x"
      borderBottomWidth={borderWidth.secondary}
      borderBottomStyle="solid"
      borderBottomColor={colors.separator.primary}
    >
      <Box pb="2x">{props.children}</Box>
    </Box>
  );
};

type ReviewsLayoutProps = {
  children: React.ReactNode;
  hasBorderBottom?: boolean;
};

export const ReviewsLayout = ({
  children,
  hasBorderBottom = true,
}: ReviewsLayoutProps): JSX.Element => {
  const theme = useTheme();
  const borderBottom = hasBorderBottom ? `1px solid ${theme.colors.separator.primary}` : undefined;
  return (
    <Box borderBottom={borderBottom} pb="10x" mb="10x">
      <Flex id="reviews" sx={{ scrollMarginTop: SCROLL_MARGIN_WITH_SEPARATOR }}>
        {children}
      </Flex>
    </Box>
  );
};
